import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Area, AreaChart } from 'recharts';
import styles from './style.module.css';
import Typography from '../../../../components/Typography/Typography';
import { SellerDashboardDropIcon } from '../../../../assets/svgs';
import { useGetSellerDashboardSalesPerformanceQuery } from '../../../../redux/services/dashboardApi';
import type { RootState } from '../../../../redux/store';

interface ChartData {
  name: string;
  value: number;
  highlight?: boolean;
}

interface SalesData {
  day: string;
  value: number;
}

interface DropdownOption {
  value: string;
  label: string;
}

interface CustomDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: DropdownOption[];
  className?: string;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({ 
  value, 
  onChange, 
  options, 
  className = '' 
}) => {
  return (
    <div className={`${styles.dropdownContainer} ${className}`}>
      <Typography
        variant="bodySmall"
        fontFamily="satoshi"
        weight="regular"
        className={styles.srOnly}
      >
        <select
          title='dropdown'
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={styles.dropdown}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <div className={styles.dropdownArrow}>
          <img src={SellerDashboardDropIcon} alt="dropdown icon" width={24} height={24} />
        </div>
      </Typography>
    </div>
  );
};

const ChartSkeleton: React.FC = () => (
  <div className={styles.chartCard}>
    <div className={styles.chartHeader}>
      <div className={styles.chartTitleSection}>
        <div className={styles.skeletonTitle}></div>
        <div className={styles.skeletonSubtitle}></div>
      </div>
      <div className={styles.skeletonDropdown}></div>
    </div>
    <div className={styles.skeletonChart}></div>
  </div>
);

const StorePerformanceChart: React.FC<{
  data: ChartData[];
  period: string;
  onPeriodChange: (period: string) => void;
  periodOptions: DropdownOption[];
}> = ({ data, period, onPeriodChange, periodOptions }) => {
  
  const CustomBar = (props: any) => {
    const { x, y, width, height, payload } = props;
    const maxValue = Math.max(...data.map(d => d.value));
    const isHighlight = payload.value === maxValue && maxValue > 0;
    const barFill = isHighlight ? '#3B82F6' : '#E5E7EB';
    const radius = 8;
    
    return (
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={barFill}
        rx={radius}
        ry={radius}
      />
    );
  };

  const maxValue = Math.max(...data.map(d => d.value), 10);
  const yAxisMax = Math.ceil(maxValue * 1.2);
  const tickInterval = Math.ceil(yAxisMax / 5);
  const ticks = Array.from({ length: 6 }, (_, i) => i * tickInterval);

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartHeader}>
        <div className={styles.chartTitleSection}>
          <Typography
            variant="heading3"
            fontFamily="satoshi"
            weight="bold"
            className={styles.chartTitle}
          >
            Store Performance
          </Typography>
          <Typography
            variant="bodySmall"
            fontFamily="satoshi"
            weight="regular"
            className={styles.chartSubtitle}
          >
            Performance of store from store visit, follow, rating and sales
          </Typography>
        </div>
        <CustomDropdown
          value={period}
          onChange={onPeriodChange}
          options={periodOptions}
        />
      </div>

      <div className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 0, right: 0, left: -33, bottom: -12 }}
            barCategoryGap="20%"
          >
            <XAxis 
              dataKey="name" 
              axisLine={true}
              opacity={0.8}
              tickLine={false}
              tick={{ fontSize: 12, fontFamily: 'Satoshi', fontWeight: 400, fill: '#1D1D1C' }}
              dy={0}
            />
            <YAxis 
              axisLine={true}
              opacity={0.8}
              tickLine={false}
              tick={{ fontSize: 12, fontFamily: 'Satoshi', fontWeight: 400, fill: '#1D1D1C' }}
              domain={[0, yAxisMax]}
              ticks={ticks}
              dx={-4}
            />
            <Bar 
              dataKey="value" 
              shape={CustomBar}
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const SalesGrowthChart: React.FC<{
  data: SalesData[];
  period: string;
  totalSales: number;
  onPeriodChange: (period: string) => void;
  periodOptions: DropdownOption[];
}> = ({ data, period, totalSales, onPeriodChange, periodOptions }) => {
  
  const calculateGrowth = (): number => {
    if (data.length < 2) return 0;
    const firstValue = data[0].value;
    const lastValue = data[data.length - 1].value;
    if (firstValue === 0) return lastValue > 0 ? 100 : 0;
    return Math.round(((lastValue - firstValue) / firstValue) * 100);
  };

  const formatAmount = (value: number): string => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
    return value.toFixed(0);
  };

  const growthPercentage = calculateGrowth();
  
  const maxValue = Math.max(...data.map(d => d.value), 10);
  const yAxisMax = Math.ceil(maxValue * 1.2);
  const tickInterval = Math.ceil(yAxisMax / 3);
  const ticks = Array.from({ length: 4 }, (_, i) => i * tickInterval);

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartHeader}>
        <div className={styles.chartTitleSection}>
          <Typography
            variant="heading3"
            fontFamily="satoshi"
            weight="bold"
            className={styles.chartTitle}
          >
            Sales Growth
          </Typography>
          <Typography
            variant="bodySmall"
            fontFamily="satoshi"
            weight="regular"
            className={styles.chartSubtitle}
          >
            Sales performance and growth
          </Typography>
        </div>
        <CustomDropdown
          value={period}
          onChange={onPeriodChange}
          options={periodOptions}
        />
      </div>

      <div className={styles.salesMetrics}>
        <Typography
          variant="heading1"
          fontFamily="satoshi"
          weight="bold"
          className={styles.salesPercentage}
        >
          {growthPercentage >= 0 ? '+' : ''}{growthPercentage}%
        </Typography>
        <div className={styles.salesAmount}>
          <Typography
            variant="bodySmall"
            fontFamily="satoshi"
            weight="regular"
            className={styles.currencySymbol}
          >
            ₦
          </Typography>
          <Typography
            variant="bodySmall"
            fontFamily="satoshi"
            weight="regular"
            className={styles.amountText}
          >
            {formatAmount(totalSales)}
          </Typography>
        </div>
      </div>

      <div className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            margin={{ top: 20, right: 60, left: -10, bottom: 0 }}
          >
            <defs>
              <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <XAxis 
              dataKey="day" 
              axisLine={true}
              opacity={0.8}
              tickLine={false}
              tick={{ fontSize: 12, fontFamily: 'Satoshi', fontWeight: 400, fill: '#1D1D1C' }}
              dy={5}
              dx={10}
            />
            <YAxis 
              axisLine={true} 
              opacity={0.8}
              tickLine={false}
              tick={{ fontSize: 12, fontFamily: 'Satoshi', fontWeight: 400, fill: '#1D1D1C' }}
              domain={[0, yAxisMax]}
              ticks={ticks}
              dx={-4}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke="#3B82F6"
              strokeWidth={3}
              fill="url(#salesGradient)"
              fillOpacity={1}
              dot={{ fill: '#3B82F6', stroke: '#FFFFFF', strokeWidth: 2, r: 4}}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export const HomeAnalyticsCharts: React.FC = () => {
  const [period, setPeriod] = useState<'week' | 'month' | 'year'>('week');
  const selectedStoreId = useSelector((state: RootState) => state.selectedStore.storeId);

  const { data, isLoading, isError, error, refetch } = useGetSellerDashboardSalesPerformanceQuery({
    period,
    ...(selectedStoreId !== 'all' && { store_id: selectedStoreId }),
  });

  const periodOptions: DropdownOption[] = [
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' },
    { value: 'year', label: 'Year' }
  ];

  const { storePerformanceData, salesGrowthData } = useMemo(() => {
    if (!data?.data) {
      return {
        storePerformanceData: [],
        salesGrowthData: []
      };
    }

    const storePerformanceData: ChartData[] = data.data.map(item => ({
      name: item.label,
      value: item.total_orders,
      highlight: false
    }));

    const salesGrowthData: SalesData[] = data.data.map(item => ({
      day: item.label,
      value: item.total_amount
    }));

    return { storePerformanceData, salesGrowthData };
  }, [data]);

  const handlePeriodChange = (newPeriod: string) => {
    setPeriod(newPeriod as 'week' | 'month' | 'year');
  };

  if (isLoading) {
    return (
      <div className={styles.analyticsContainer}>
        <div className={styles.chartsGrid}>
          <ChartSkeleton />
          <ChartSkeleton />
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className={styles.analyticsContainer}>
        <div className={styles.errorContainer}>
          <div className={styles.errorIcon}>⚠️</div>
          <Typography variant="heading3" fontFamily="satoshi" weight="bold">
            Failed to load analytics charts
          </Typography>
          <Typography variant="bodyRegular" fontFamily="satoshi" weight="regular">
            {error && 'status' in error
              ? `Error: ${error.status}`
              : 'Unable to fetch chart data. Please try again.'}
          </Typography>
          <button onClick={() => refetch()} className={styles.retryButton}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.analyticsContainer}>
      <div className={styles.chartsGrid}>
        <StorePerformanceChart
          data={storePerformanceData}
          period={period}
          onPeriodChange={handlePeriodChange}
          periodOptions={periodOptions}
        />
        
        <SalesGrowthChart
          data={salesGrowthData}
          period={period}
          totalSales={data.total_sales}
          onPeriodChange={handlePeriodChange}
          periodOptions={periodOptions}
        />
      </div>
    </div>
  );
};